'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { ViewerSource } from './types';

/** 'overlay' — the panel covers the page. 'embedded' — the page makes room. */
export type ViewerDockStyle = 'overlay' | 'embedded';

const DOCK_STYLE_KEY = 'document-viewer:dock-style';

/** Whether the guidance rail is open or reduced to a slim strip. */
export type ViewerGuidanceRail = 'expanded' | 'collapsed';

const GUIDANCE_RAIL_KEY = 'document-viewer:guidance-rail';

/**
 * Panel width, per dock style, in px.
 *
 * One number, owned here, read by BOTH the panel (its own width) and the
 * dashboard shell (the margin it reserves for an embedded panel). It used to be
 * a CSS `min()` string the two had to agree on — and when the shell stopped
 * reading it (the 2026-09-21 design port dropped the reservation), the embedded
 * panel quietly went back to covering the page. A shared number that the shell
 * reads through `reservedRight` cannot drift that way.
 */
export const VIEWER_MIN_WIDTH = 320;
/** Matches the overlay panel's historical `max-w-3xl`. */
export const VIEWER_DEFAULT_WIDTH = 768;
/** Fraction of the viewport the panel may take. */
export const VIEWER_MAX_VW = 0.7;
/** Default embedded width as a fraction of the viewport (was `60vw`). */
export const VIEWER_EMBED_DEFAULT_VW = 0.6;
/**
 * What an embedded panel must always leave for the page beside it: the
 * collapsed sidebar plus a readable content column.
 */
export const VIEWER_MIN_PAGE_PX = 560;
/** Below this, a floating panel is a full-width sheet, as it always was. */
const NARROW_VIEWPORT_PX = 640;
/** Used before mount, when there is no window to measure (SSR). */
const FALLBACK_VIEWPORT_PX = 1280;
/** Keyboard step for the resize handle. */
export const VIEWER_RESIZE_STEP = 16;

const WIDTH_KEY: Record<ViewerDockStyle, string> = {
  overlay: 'document-viewer:width:overlay',
  embedded: 'document-viewer:width:embedded',
};

export interface ViewerWidthBounds {
  min: number;
  max: number;
  default: number;
}

/**
 * Bounds for the panel's width in a given dock style. Pure, so the clamp the
 * handle applies and the width the shell reserves come from one formula.
 */
export function viewerWidthBounds(
  style: ViewerDockStyle,
  viewportWidth: number,
  insetRight = 0,
): ViewerWidthBounds {
  const vw = viewportWidth > 0 ? viewportWidth : FALLBACK_VIEWPORT_PX;
  const min = Math.min(VIEWER_MIN_WIDTH, vw);
  let max: number;
  let def: number;
  if (style === 'embedded') {
    // Never squeeze the page beside it below a usable column, and never let
    // the AI box's inset push the panel past the viewport.
    max = Math.floor(Math.min(vw * VIEWER_MAX_VW, vw - insetRight - VIEWER_MIN_PAGE_PX));
    def = Math.floor(Math.min(VIEWER_DEFAULT_WIDTH, vw * VIEWER_EMBED_DEFAULT_VW));
  } else {
    max = vw < NARROW_VIEWPORT_PX ? vw : Math.floor(vw * VIEWER_MAX_VW);
    def = VIEWER_DEFAULT_WIDTH;
  }
  max = Math.max(min, max);
  return { min, max, default: clamp(def, min, max) };
}

function clamp(n: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, n));
}

function readStoredWidth(style: ViewerDockStyle): number | null {
  try {
    const raw = localStorage.getItem(WIDTH_KEY[style]);
    if (raw === null) return null;
    const n = Number(raw);
    // A corrupt value is ignored, not trusted: NaN or a negative width would
    // otherwise collapse the panel until the caseworker found the reset.
    return Number.isFinite(n) && n > 0 ? Math.round(n) : null;
  } catch {
    return null;
  }
}

interface DocumentViewerContextValue {
  source: ViewerSource | null;
  open: (source: ViewerSource) => void;
  close: () => void;
  dockStyle: ViewerDockStyle;
  setDockStyle: (style: ViewerDockStyle) => void;
  /** Guidance rail state; lives here so it survives closing and re-opening. */
  guidanceRail: ViewerGuidanceRail;
  setGuidanceRail: (state: ViewerGuidanceRail) => void;
  /** True when the page should make room: embedded AND something is open. */
  embedded: boolean;
  /**
   * How far the panel sits in from the right edge. Published BY the dashboard
   * shell, which is the only thing that knows the AI box is also embedded —
   * the panel renders above that provider and cannot read it. Without this the
   * two embedded panels would occupy the same edge and stack.
   */
  embedInsetRight: number;
  setEmbedInsetRight: (px: number) => void;
  /** The panel's current width (px) in the current dock style, clamped. */
  panelWidth: number;
  /** Bounds for `panelWidth` at the current viewport, for the resize handle. */
  widthBounds: ViewerWidthBounds;
  /**
   * Set the width for the CURRENT dock style. Clamped. `persist` writes it to
   * storage — the handle passes it once a gesture ends, not on every move.
   */
  setPanelWidth: (px: number, opts?: { persist?: boolean }) => void;
  /** Back to the default width for the current dock style; forgets the stored one. */
  resetPanelWidth: () => void;
  /** True while the handle is being dragged — the shell drops its margin transition. */
  resizing: boolean;
  setResizing: (on: boolean) => void;
  /**
   * Width (px) the page must keep clear on its right for the viewer: the
   * panel width while embedded and open, else 0. The shell adds the AI box's
   * own inset on top.
   */
  reservedRight: number;
  /**
   * "Show me this text." Set by a caller that holds a piece of the document's
   * text — a field's value, a section heading — and wants it on screen. The
   * PANEL resolves it, because the panel owns the extracted page text and the
   * page jump; this is a request, not a location. `nonce` lets a repeat
   * request for the same text still fire.
   */
  locateRequest: { needle: string; pdfUrl: string; nonce: number } | null;
  /**
   * `pdfUrl` names WHICH document the text belongs to. The panel ignores a
   * request for a document it is not showing, and the caller that owns that
   * document (DocumentPreview) opens it in response — so clicking a field on
   * a step whose viewer is closed opens the viewer AND lands on the field.
   */
  requestLocate: (needle: string, pdfUrl: string) => void;
}

const DocumentViewerContext = createContext<DocumentViewerContextValue | null>(null);

/**
 * One panel per page. `open` on an already-open panel SWAPS the content rather
 * than stacking — a caseworker clicking a second document expects to be looking
 * at the second document, not managing windows.
 */
export function DocumentViewerProvider({ children }: { children: ReactNode }) {
  const [source, setSource] = useState<ViewerSource | null>(null);
  const open = useCallback((next: ViewerSource) => setSource(next), []);
  const close = useCallback(() => setSource(null), []);

  // Starts as 'overlay' and is corrected after mount rather than read during
  // render: reading localStorage in the initialiser makes the server and the
  // first client render disagree, which is a hydration mismatch.
  const [dockStyle, setDockStyleState] = useState<ViewerDockStyle>('overlay');
  useEffect(() => {
    try {
      const raw = localStorage.getItem(DOCK_STYLE_KEY);
      if (raw === 'overlay' || raw === 'embedded') setDockStyleState(raw);
    } catch {
      // Private mode / blocked storage — the default stands.
    }
  }, []);
  const setDockStyle = useCallback((next: ViewerDockStyle) => {
    setDockStyleState(next);
    try {
      localStorage.setItem(DOCK_STYLE_KEY, next);
    } catch {
      // Preference simply does not persist; the session still works.
    }
  }, []);

  // Same pattern as the dock style: default in state, stored value read after
  // mount (hydration-safe), writes best-effort.
  const [guidanceRail, setGuidanceRailState] = useState<ViewerGuidanceRail>('expanded');
  useEffect(() => {
    try {
      const raw = localStorage.getItem(GUIDANCE_RAIL_KEY);
      if (raw === 'expanded' || raw === 'collapsed') setGuidanceRailState(raw);
    } catch {
      // Private mode / blocked storage — the default stands.
    }
  }, []);
  const setGuidanceRail = useCallback((next: ViewerGuidanceRail) => {
    setGuidanceRailState(next);
    try {
      localStorage.setItem(GUIDANCE_RAIL_KEY, next);
    } catch {
      // Preference simply does not persist; the session still works.
    }
  }, []);

  const [embedInsetRight, setEmbedInsetRight] = useState(0);

  // Width per dock style. `null` = "the default", so a caseworker who never
  // resized follows the viewport rather than a number frozen at first open.
  // Read after mount, like the preferences above (hydration-safe).
  const [storedWidths, setStoredWidths] = useState<Record<ViewerDockStyle, number | null>>({
    overlay: null,
    embedded: null,
  });
  useEffect(() => {
    setStoredWidths({ overlay: readStoredWidth('overlay'), embedded: readStoredWidth('embedded') });
  }, []);

  // 0 until mounted; `viewerWidthBounds` substitutes a desktop width for it.
  const [viewportWidth, setViewportWidth] = useState(0);
  useEffect(() => {
    const measure = () => setViewportWidth(window.innerWidth);
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  const widthBounds = useMemo(
    () => viewerWidthBounds(dockStyle, viewportWidth, embedInsetRight),
    [dockStyle, viewportWidth, embedInsetRight],
  );
  // Clamped at READ time too: a width stored on a wide monitor must not
  // overflow a laptop, and the stored value is kept for when it fits again.
  const panelWidth = clamp(
    storedWidths[dockStyle] ?? widthBounds.default,
    widthBounds.min,
    widthBounds.max,
  );

  const setPanelWidth = useCallback(
    (px: number, opts?: { persist?: boolean }) => {
      if (!Number.isFinite(px)) return;
      const next = Math.round(clamp(px, widthBounds.min, widthBounds.max));
      setStoredWidths((prev) => (prev[dockStyle] === next ? prev : { ...prev, [dockStyle]: next }));
      if (opts?.persist) {
        try {
          localStorage.setItem(WIDTH_KEY[dockStyle], String(next));
        } catch {
          // Preference simply does not persist; the session still works.
        }
      }
    },
    [dockStyle, widthBounds.min, widthBounds.max],
  );
  const resetPanelWidth = useCallback(() => {
    setStoredWidths((prev) => ({ ...prev, [dockStyle]: null }));
    try {
      localStorage.removeItem(WIDTH_KEY[dockStyle]);
    } catch {
      // Nothing stored that we can reach; the default stands either way.
    }
  }, [dockStyle]);

  const [resizing, setResizing] = useState(false);

  const [locateRequest, setLocateRequest] = useState<{
    needle: string;
    pdfUrl: string;
    nonce: number;
  } | null>(null);
  const requestLocate = useCallback((needle: string, pdfUrl: string) => {
    setLocateRequest((prev) => ({ needle, pdfUrl, nonce: (prev?.nonce ?? 0) + 1 }));
  }, []);
  // A locate belongs to the document it was asked about. Opening a different
  // document must not carry a stale request across, or the new document would
  // jump to wherever its text happened to contain the old needle.
  const openClearingLocate = useCallback(
    (next: ViewerSource) => {
      setLocateRequest((prev) => (prev && source?.pdfUrl !== next.pdfUrl ? null : prev));
      open(next);
    },
    [open, source?.pdfUrl],
  );

  const embedded = dockStyle === 'embedded' && source !== null;

  const value = useMemo(
    () => ({
      source,
      open: openClearingLocate,
      close,
      dockStyle,
      setDockStyle,
      guidanceRail,
      setGuidanceRail,
      // A closed viewer must never push the page, whatever the stored
      // preference says.
      embedded,
      embedInsetRight,
      setEmbedInsetRight,
      panelWidth,
      widthBounds,
      setPanelWidth,
      resetPanelWidth,
      resizing,
      setResizing,
      reservedRight: embedded ? panelWidth : 0,
      locateRequest,
      requestLocate,
    }),
    [
      source,
      openClearingLocate,
      close,
      dockStyle,
      setDockStyle,
      guidanceRail,
      setGuidanceRail,
      embedded,
      embedInsetRight,
      panelWidth,
      widthBounds,
      setPanelWidth,
      resetPanelWidth,
      resizing,
      locateRequest,
      requestLocate,
    ],
  );

  return (
    <DocumentViewerContext.Provider value={value}>{children}</DocumentViewerContext.Provider>
  );
}

export function useDocumentViewer(): DocumentViewerContextValue {
  const ctx = useContext(DocumentViewerContext);
  if (!ctx) {
    throw new Error('useDocumentViewer must be used inside <DocumentViewerProvider>');
  }
  return ctx;
}
